import React, { useState, useEffect } from 'react'
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon, CheckIcon } from '@heroicons/react/24/outline'

interface Step {
  id: string
  title: string
  content: string
  target?: string
  placement?: 'top' | 'bottom' | 'left' | 'right'
  action?: {
    text: string
    onClick: () => void
  }
}

interface OnboardingGuideProps {
  steps: Step[]
  isOpen: boolean
  onComplete: () => void
  onSkip: () => void
}

export const OnboardingGuide: React.FC<OnboardingGuideProps> = ({
  steps,
  isOpen,
  onComplete,
  onSkip
}) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [highlightedElement, setHighlightedElement] = useState<HTMLElement | null>(null)

  useEffect(() => {
    if (isOpen && steps[currentStep]?.target) {
      const element = document.querySelector(steps[currentStep].target!) as HTMLElement
      setHighlightedElement(element)
    } else {
      setHighlightedElement(null)
    }
  }, [currentStep, isOpen, steps])

  if (!isOpen) return null

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1)
    } else {
      onComplete()
    }
  }

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleStepClick = (stepIndex: number) => {
    setCurrentStep(stepIndex)
  }

  const currentStepData = steps[currentStep]

  return (
    <>
      {/* 遮罩层 */}
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onSkip} />
      
      {/* 高亮区域 */}
      {highlightedElement && (
        <div
          className="fixed bg-white rounded-lg shadow-2xl z-[60] ring-4 ring-blue-500 ring-opacity-50"
          style={{
            top: highlightedElement.offsetTop - 8,
            left: highlightedElement.offsetLeft - 8,
            width: highlightedElement.offsetWidth + 16,
            height: highlightedElement.offsetHeight + 16,
            pointerEvents: 'none'
          }}
        />
      )}

      {/* 引导卡片 */}
      <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[70] w-full max-w-md mx-4">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* 头部 */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold">{currentStepData.title}</h3>
              <button
                onClick={onSkip}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            {/* 步骤指示器 */}
            <div className="flex items-center mt-4 space-x-2">
              {steps.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleStepClick(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === currentStep 
                      ? 'bg-white' 
                      : index < currentStep 
                        ? 'bg-white/70' 
                        : 'bg-white/30'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* 内容 */}
          <div className="p-6">
            <p className="text-gray-700 leading-relaxed mb-6">
              {currentStepData.content}
            </p>

            {/* 自定义操作按钮 */}
            {currentStepData.action && (
              <div className="mb-6">
                <button
                  onClick={currentStepData.action.onClick}
                  className="w-full bg-blue-50 text-blue-600 border-2 border-blue-200 px-4 py-3 rounded-lg hover:bg-blue-100 transition-colors font-medium"
                >
                  {currentStepData.action.text}
                </button>
              </div>
            )}

            {/* 导航按钮 */}
            <div className="flex items-center justify-between">
              <button
                onClick={handlePrev}
                disabled={currentStep === 0}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                  currentStep === 0
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <ChevronLeftIcon className="h-5 w-5 mr-1" />
                上一步
              </button>

              <span className="text-sm text-gray-500">
                {currentStep + 1} / {steps.length}
              </span>

              <button
                onClick={handleNext}
                className="flex items-center bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                {currentStep === steps.length - 1 ? (
                  <>
                    <CheckIcon className="h-5 w-5 mr-1" />
                    完成
                  </>
                ) : (
                  <>
                    下一步
                    <ChevronRightIcon className="h-5 w-5 ml-1" />
                  </>
                )}
              </button>
            </div>
          </div>

          {/* 底部跳过按钮 */}
          <div className="bg-gray-50 px-6 py-3 text-center border-t">
            <button
              onClick={onSkip}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              跳过引导
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

// 学生仪表板引导步骤
export const studentDashboardSteps: Step[] = [
  {
    id: 'welcome',
    title: '欢迎来到学习平台！',
    content: '这里是您的个人学习中心，让我们一起了解主要功能。',
  },
  {
    id: 'courses',
    title: '浏览课程',
    content: '在这里您可以浏览所有可用的课程，按分类查找适合您的学习内容。',
    target: '[data-guide="courses"]'
  },
  {
    id: 'booking',
    title: '预约课程',
    content: '找到心仪的课程后，点击预约按钮选择合适的时间进行一对一学习。',
    target: '[data-guide="booking"]'
  },
  {
    id: 'schedule',
    title: '查看课程表',
    content: '在课程表中可以查看您已预约的所有课程安排。',
    target: '[data-guide="schedule"]'
  },
  {
    id: 'profile',
    title: '个人设置',
    content: '在个人设置中可以更新您的个人信息和学习偏好。',
    target: '[data-guide="profile"]'
  }
]

// 首次登录引导步骤
export const firstLoginSteps: Step[] = [
  {
    id: 'welcome',
    title: '欢迎加入我们！',
    content: '感谢您选择我们的学习平台。让我们快速了解一下主要功能，帮您更好地开始学习之旅。',
  },
  {
    id: 'complete-profile',
    title: '完善个人资料',
    content: '建议先完善您的个人资料，这有助于老师更好地了解您的学习需求。',
    action: {
      text: '去完善资料',
      onClick: () => window.location.href = '/student/profile'
    }
  },
  {
    id: 'browse-courses',
    title: '浏览课程',
    content: '我们有丰富的课程选择，从基础入门到高级进阶，总有适合您的课程。',
    action: {
      text: '浏览课程',
      onClick: () => window.location.href = '/courses'
    }
  }
]

// 新手引导钩子
export const useOnboarding = (key: string) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isCompleted, setIsCompleted] = useState(false)

  useEffect(() => {
    const completed = localStorage.getItem(`onboarding-${key}`)
    if (!completed) {
      setIsOpen(true)
    } else {
      setIsCompleted(true)
    }
  }, [key])

  const complete = () => {
    localStorage.setItem(`onboarding-${key}`, 'true')
    setIsOpen(false)
    setIsCompleted(true)
  }

  const skip = () => {
    localStorage.setItem(`onboarding-${key}`, 'true')
    setIsOpen(false)
    setIsCompleted(true)
  }

  const reset = () => {
    localStorage.removeItem(`onboarding-${key}`)
    setIsOpen(true)
    setIsCompleted(false)
  }

  return {
    isOpen,
    isCompleted,
    complete,
    skip,
    reset
  }
}

export default OnboardingGuide 